import { InfoCircleOutlined } from '@ant-design/icons';
import { landmineNames } from '@mm/constants/landmine-names';
import { useCompanyCRUDActions } from '@mm/state/actions/CompanyCRUDActions';
import modalActions from '@mm/state/modules/modalsState/actions';
import { combinedFeatureFlagsSelector } from '@mm/state/selectors/featureFlagsSelectors';
import { activeCompanySelector } from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dispatch } from 'redux';
import ImageUploader from '../../../../common/components/ImageUploader/ImageUploader';
import {
  BadgeTheme,
  MonkeyBadge,
} from '../../../../common/components/MonkeyBadge/MonkeyBadge';
import TextInput from '../../../../common/components/TextInput';
import { SettingsSectionHeader } from '../common/SettingsSectionHeader/SettingsSectionHeader';
import { ReactComponent as BusinessIcon } from 'assets/business.svg';
import './CompanyPanel.pcss';
import { useCreateCompanyWarningModal } from './use-create-company-warning-modal';
import { useWhitelabelConfig } from '@mm/hooks';
import { InputWithClipboard } from '../../../../editor/features/automation/common/InputWithClipboard/InputWithClipboard';
import { routes } from '../../../../modules/LoginRouter/routes';

export const CompanyPanel = () => {
  const dispatch: Dispatch<StoreState> = useDispatch();
  const activeCompany = useSelector(activeCompanySelector);
  const activeCompanyId = activeCompany?.id;
  const activeCompanyName = activeCompany?.name;
  const activeCompanyEditable = activeCompany?.editable;

  const canUploadCompanyLogo = useSelector(
    combinedFeatureFlagsSelector,
  ).settingsCompanyLogoUpload;
  const activeCompanyLogo = activeCompany?.logo?.urls?.medium || null;

  const [companyName, setCompanyName] = useState(activeCompanyName);
  const [hasPermission, setHasPermission] = useState(activeCompanyEditable);

  const { update } = useCompanyCRUDActions();
  const { showCreateCompanyWarningModal } = useCreateCompanyWarningModal();

  const onCompanyNameUpdate = () => {
    if (companyName && companyName !== activeCompanyName) {
      update({ ...activeCompany, name: companyName });
    }
  };

  const { isWhitelabelCompany, companyHash } = useWhitelabelConfig();

  useEffect(() => {
    setCompanyName(activeCompanyName);
    setHasPermission(activeCompanyEditable);
  }, [activeCompanyId, activeCompanyName, activeCompanyEditable]);

  const updateCompanyAssetId = (assetId: number | null) => {
    update({ ...activeCompany, assetId });
  };

  const onCompanyLogoUpload = (assetId: number) =>
    updateCompanyAssetId(assetId);

  const onRemove = () => updateCompanyAssetId(null);

  const promptTeamPlanIfNeeded = () =>
    !canUploadCompanyLogo &&
    dispatch(
      modalActions.openUniversalUpgradeLandmineModal({
        landmineName: landmineNames['SETTINGS__COMPANY-PANEL'],
        feature: 'settingsCompanyLogoUpload',
      }),
    );

  return (
    <div className="company-panel">
      <SettingsSectionHeader
        icon={<BusinessIcon />}
        color="#597EF7"
        title={`${companyName} Company`}
        headerComponent={
          <div
            key="company-panel_create-company-button"
            className="company-panel_create-company-button"
            onClick={showCreateCompanyWarningModal}
          >
            Create New Company
          </div>
        }
      />

      <div className="company-panel__section">
        <div className="company-panel__section-block">
          <div className="company-panel__section-block-title">Company name</div>
          <TextInput
            value={companyName}
            disabled={!hasPermission}
            onChange={e => setCompanyName(e.target.value)}
            onBlur={onCompanyNameUpdate}
            onMouseLeave={onCompanyNameUpdate}
            maxLength={50}
            className="company-panel__input"
          />
        </div>
        {isWhitelabelCompany && (
          <div style={{ width: 600 }}>
            <div className="company-panel__section-block-title">
              White Label Signup Page
            </div>
            <InputWithClipboard
              buttonName="Copy"
              value={`${
                IS_PROD
                  ? 'https://app.mobilemonkey.com'
                  : window.location.origin
              }${routes.signup.compile(companyHash)}`}
              title=""
            />
          </div>
        )}
        <div className="company-panel__section-block">
          <div className="company-panel__section-block-title">
            Company logo for signup page{' '}
            {!canUploadCompanyLogo && (
              <MonkeyBadge
                className="company-panel__team-badge"
                theme={BadgeTheme.greenGradient}
              >
                AGENCY
              </MonkeyBadge>
            )}
          </div>
          <div
            className="company-panel__pane-content company-panel__pane-content-logo"
            onClick={promptTeamPlanIfNeeded}
          >
            <ImageUploader
              disabled={!canUploadCompanyLogo || !hasPermission}
              file={activeCompanyLogo}
              className="company-panel__logo-form"
              onUpload={onCompanyLogoUpload}
              disableCrop={true}
              withRemoveBtn={true}
              onRemove={onRemove}
              newStyle
            />
            <div className="company-panel__hint">
              <InfoCircleOutlined />
              <div className="company-panel__hint-texts-wrapper">
                <span className="company-panel__hint-text">
                  Supported formats: JPG, JPEG, PNG, GIF
                </span>
                <span className="company-panel__hint-text">
                  {' '}
                  Recommended dimensions: 250x130px / 500x260px
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
